import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { IoClose, IoPhonePortraitOutline } from "react-icons/io5";
import { useInstallOption, startInstall } from "../utils/installPrompt";

// A one-tap "Install GatherYou" strip for phones, so a friend who just opened
// the link doesn't have to find the option inside the browser's menu. Shown
// only while the browser says the app can be installed (never once it is),
// hidden on the install page itself (which has its own big button), and
// waved away for two weeks once dismissed.
const DISMISS_KEY = "virsa_install_banner_dismissed";
const HIDE_MS = 14 * 24 * 60 * 60 * 1000;

function wasDismissedRecently() {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY));
    return at > 0 && Date.now() - at < HIDE_MS;
  } catch {
    return false;
  }
}

export default function InstallBanner() {
  const location = useLocation();
  const option = useInstallOption();
  const [dismissed, setDismissed] = useState(wasDismissedRecently);

  const [isPhoneSized, setIsPhoneSized] = useState(
    () => window.matchMedia?.("(max-width: 820px)").matches ?? false
  );

  // A plain read at render time never updated if the viewport crossed the
  // breakpoint without some other state change triggering a re-render (a
  // desktop window resize, or rotating a foldable).
  useEffect(() => {
    const mq = window.matchMedia?.("(max-width: 820px)");
    if (!mq) return;

    function onChange(e) {
      setIsPhoneSized(e.matches);
    }

    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else if (mq.addListener) mq.addListener(onChange);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);

  if (!option || dismissed || !isPhoneSized || location.pathname === "/install") return null;

  function dismiss() {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // storage blocked — it just comes back next visit
    }
    setDismissed(true);
  }

  return (
    <div style={banner}>
      <IoPhonePortraitOutline size={18} style={{ flexShrink: 0 }} />
      <span style={text}>Get the GatherYou app</span>
      <button style={installBtn} onClick={() => startInstall(option)}>
        Install
      </button>
      <button style={closeBtn} onClick={dismiss} aria-label="Dismiss">
        <IoClose size={18} />
      </button>
    </div>
  );
}

// Normal flow (not position:fixed), like UpdateBanner and OfflineBanner: it
// pushes Navbar's sticky top bar down instead of covering it.
const banner = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 10,
  background: "var(--accent-primary)",
  color: "#fff",
  fontSize: 13,
  fontWeight: 600,
  padding: "6px 10px"
};

const text = {
  minWidth: 0
};

const installBtn = {
  padding: "4px 14px",
  borderRadius: 8,
  border: "1px solid rgba(255,255,255,0.5)",
  background: "rgba(255,255,255,0.18)",
  color: "#fff",
  fontWeight: 700,
  fontSize: 12.5,
  cursor: "pointer"
};

const closeBtn = {
  display: "flex",
  alignItems: "center",
  padding: 2,
  background: "none",
  border: "none",
  color: "#fff",
  cursor: "pointer",
  opacity: 0.85
};
